// Walk-in registration — RON-5469 R3. Prototype region: `#bd-walkin`.
//
// ONE CALL registers the person and checks them in (§5). There is deliberately no second step:
//
//   "A walk-in is standing at the desk — creating them `expected` and making the receptionist
//    press a second button would misreport who is in the building for as long as it took."
//
// So the submit button says what actually happens, and the dialog closes onto a board where the
// person is already on site.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GetReceptionHosts_api, isSuccessfulResponse } from "../ReceptionAPI.jsx";

// Long enough to swallow typing a surname, short enough not to feel laggy at a desk.
const HOST_SEARCH_DEBOUNCE_MS = 250;

const EMPTY = {
  name: "",
  company: "",
  email: "",
  purpose: "",
  expectedOut: "",
  isVip: false,
  requireHs: true,
  requireNda: false,
};

const ReceptionWalkInModal = ({ isOpen, isBusy, onSubmit, onClose }) => {
  const { t } = useTranslation("common");
  const [form, setForm] = useState(EMPTY);
  const [host, setHost] = useState(null);
  const [hostQuery, setHostQuery] = useState("");
  const [hostResults, setHostResults] = useState([]);
  const [errors, setErrors] = useState({});

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  // A fresh dialog each time it opens. Without this, the last walk-in's details are still in the
  // boxes for the next person — and at a desk they would get submitted.
  useEffect(() => {
    if (!isOpen) return;

    setForm(EMPTY);
    setHost(null);
    setHostQuery("");
    setHostResults([]);
    setErrors({});
  }, [isOpen]);

  // ── Host type-ahead (§5) ───────────────────────────────────────────────────────────────────
  //
  // `GET /visitor/reception/hosts`, NOT `/visitor/hosts/candidates` — that one is gated on
  // `vcm.host.view`, which is admin-only, so a receptionist gets a 403.
  //
  // It lists every ACTIVE EMPLOYEE, not only designated hosts: "a visitor asks for a person by
  // name and that person need not hold the visitor capability". `is_host` only badges them.
  const searchToken = useRef(0);

  const searchHosts = useCallback(async (term) => {
    const token = ++searchToken.current;

    try {
      const response = await GetReceptionHosts_api({ Query: term });

      // A later keystroke already owns the list.
      if (token !== searchToken.current || !isSuccessfulResponse(response)) return;

      setHostResults(response.Data.Records.items || []);
    } catch (error) {
      console.error("GetReceptionHosts error", error);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => searchHosts(hostQuery.trim()), HOST_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [hostQuery, isOpen, searchHosts]);

  // Designated hosts first, then everyone else — both are selectable, because the API
  // deliberately does not filter non-hosts out and neither should the desk.
  const sortedHosts = useMemo(
    () =>
      [...hostResults].sort((a, b) => Number(b.is_host) - Number(a.is_host)),
    [hostResults]
  );

  if (!isOpen) return null;

  const validate = () => {
    const next = {};

    // Both are 422s server-side; caught here so the desk sees which field, not a status code.
    if (!form.name.trim()) next.name = t("react_reception_error_name");
    if (!host) next.host = t("react_reception_error_host");
    // Optional, but validated when present — a typo means the host is told about somebody the
    // system can never contact again.
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = t("react_reception_error_email");
    }

    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const submit = () => {
    if (!validate()) return;

    onSubmit?.({
      Name: form.name,
      HostID: host.id,
      Company: form.company,
      Email: form.email,
      Purpose: form.purpose,
      ExpectedOut: form.expectedOut,
      IsVip: form.isVip,
      RequireHs: form.requireHs,
      RequireNda: form.requireNda,
    });
  };

  return (
    <div className="reception-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="reception-modal reception-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={t("react_reception_walk_in_title")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reception-modal__head">
          <h2>{t("react_reception_walk_in_title")}</h2>
          <button
            type="button"
            className="reception-modal__x"
            onClick={onClose}
            aria-label={t("react_reception_close")}
          >
            ✕
          </button>
        </div>

        <p className="reception-modal__sub">{t("react_reception_walk_in_sub")}</p>

        <div className="reception-modal__body">
          <div className="reception-field__row">
            <label className="reception-field">
              <span className="reception-field__label">
                {t("name")} <span className="reception-field__req">*</span>
              </span>
              <input
                type="text"
                className="reception-field__input"
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
              />
              {errors.name && <span className="reception-field__error">{errors.name}</span>}
            </label>

            <label className="reception-field">
              <span className="reception-field__label">{t("company")}</span>
              <input
                type="text"
                className="reception-field__input"
                value={form.company}
                onChange={(event) => setField("company", event.target.value)}
              />
            </label>
          </div>

          {/* The host is REQUIRED. "Somebody at a desk is there to see a person, and a visit with
              no host is one nobody can be told about or escort." */}
          <div className="reception-field">
            <span className="reception-field__label">
              {t("react_reception_col_host")} <span className="reception-field__req">*</span>
            </span>

            {host ? (
              <div className="reception-hostpick">
                <span className="reception-hostpick__name">
                  {host.name}
                  {host.is_host && (
                    <span className="reception-tag">{t("react_reception_designated_host")}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="reception-hostpick__change"
                  onClick={() => setHost(null)}
                >
                  {t("react_reception_change")}
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="reception-field__input"
                  value={hostQuery}
                  onChange={(event) => setHostQuery(event.target.value)}
                  placeholder={t("react_reception_host_placeholder")}
                  aria-label={t("react_reception_col_host")}
                />
                <ul className="reception-hostlist">
                  {sortedHosts.map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        className="reception-hostlist__item"
                        onClick={() => {
                          setHost(candidate);
                          setErrors((current) => ({ ...current, host: undefined }));
                        }}
                      >
                        <span>{candidate.name}</span>
                        <span className="reception-hostlist__email">{candidate.email}</span>
                        {/* Marked, not filtered — a visitor can ask for anyone. */}
                        {candidate.is_host && (
                          <span className="reception-tag">
                            {t("react_reception_designated_host")}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                  {sortedHosts.length === 0 && (
                    <li className="reception-hostlist__empty">
                      {t("react_reception_no_hosts")}
                    </li>
                  )}
                </ul>
              </>
            )}

            {errors.host && <span className="reception-field__error">{errors.host}</span>}
          </div>

          <div className="reception-field__row">
            <label className="reception-field">
              <span className="reception-field__label">{t("email")}</span>
              <input
                type="email"
                className="reception-field__input"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
              />
              {errors.email ? (
                <span className="reception-field__error">{errors.email}</span>
              ) : (
                <span className="reception-field__help">
                  {t("react_reception_email_help")}
                </span>
              )}
            </label>

            <label className="reception-field">
              <span className="reception-field__label">
                {t("react_reception_expected_out")}
              </span>
              <input
                type="time"
                className="reception-field__input"
                value={form.expectedOut}
                onChange={(event) => setField("expectedOut", event.target.value)}
              />
            </label>
          </div>

          <label className="reception-field">
            <span className="reception-field__label">{t("react_reception_purpose")}</span>
            <textarea
              className="reception-field__input reception-field__input--area"
              rows={2}
              value={form.purpose}
              onChange={(event) => setField("purpose", event.target.value)}
            />
          </label>

          <div className="reception-switches">
            <label className="reception-switch">
              <input
                type="checkbox"
                checked={form.isVip}
                onChange={(event) => setField("isVip", event.target.checked)}
              />
              <span>{t("react_reception_mark_vip")}</span>
            </label>

            <label className="reception-switch">
              <input
                type="checkbox"
                checked={form.requireHs}
                onChange={(event) => setField("requireHs", event.target.checked)}
              />
              <span>{t("react_reception_require_hs")}</span>
            </label>

            <label className="reception-switch">
              <input
                type="checkbox"
                checked={form.requireNda}
                onChange={(event) => setField("requireNda", event.target.checked)}
              />
              <span>{t("react_reception_require_nda")}</span>
            </label>
          </div>

          {/* Said out loud, because the switches look like compliance and are not. A16: they are
              stored, nothing reads them yet, and they must never render as a tick in the
              compliance panel — a `1` here means "the desk said this visit needs one", not that
              anybody was asked. */}
          <p className="reception-modal__note">{t("react_reception_require_note")}</p>
        </div>

        <div className="reception-modal__foot">
          <button type="button" className="reception-btn reception-btn--ghost" onClick={onClose}>
            {t("cancel")}
          </button>
          {/* Names what actually happens: one press registers them AND checks them in. */}
          <button type="button" className="reception-btn" disabled={isBusy} onClick={submit}>
            {t("react_reception_register_and_check_in")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceptionWalkInModal;
